import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, TextArea } from 'components/common/form';
import { UsersListSelectedUsers } from 'components/users';

import type { GetCalendarEventUsersRes } from 'shared-types/responses';

interface Props {
  action?: 'grant' | 'remove';
  selectedUserIds?: number[];
  updateUsers: () => Promise<void>;
  user?: GetCalendarEventUsersRes['users'][0];
}

const CalendarEventParticipantsToggleParticipation: React.FunctionComponent<
  Props
> = ({ action, selectedUserIds, updateUsers, user }) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const [userIds, setUserIds] = useState<number[]>([]);

  const handleSubmit = async (values: { participatedComment?: string }) => {
    try {
      await api.put(
        `/admin/calendar-events/${calendarEvent.id}/mark-participation${
          user ? `/${user.id}` : ''
        }`,
        {
          ...(action ? { action } : {}),
          ...values,
          ...(selectedUserIds?.length && userIds.length ? { userIds } : {})
        }
      );
      await updateUsers();
      dispatch(actions.addNotice({ text: textInfo.success, type: 'success' }));
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  if (user && user.status !== 'added') return null;

  const textInfo =
    !user && action === 'grant'
      ? {
          category: 'success',
          icon: 'check-circle',
          success:
            messages.eventCalendarParticipantsGrantParticipationSuccessAll,
          title: messages.eventCalendarParticipantsGrantParticipationAll
        }
      : !user && action === 'remove'
      ? {
          category: 'danger',
          icon: 'ban',
          success:
            messages.eventCalendarParticipantsRemoveParticipationSuccessAll,
          title: messages.eventCalendarParticipantsRemoveParticipationAll
        }
      : user?.participated
      ? {
          category: 'danger',
          icon: 'ban',
          success: messages.eventCalendarParticipantsRemoveParticipationSuccess,
          title: messages.eventCalendarParticipantsRemoveParticipation
        }
      : {
          category: 'success',
          icon: 'check-circle',
          success: messages.eventCalendarParticipantsGrantParticipationSuccess,
          title: messages.eventCalendarParticipantsGrantParticipation
        };

  return (
    <ModalButtonWithForm
      buttonCategory={textInfo.category}
      buttonIcon={textInfo.icon}
      buttonIsDropdown={true}
      buttonText={textInfo.title}
      modalActionText={messages.commonConfirm}
      modalTitle={textInfo.title}
      modalWidth="medium"
      onSubmit={handleSubmit}
    >
      {() => (
        <>
          {selectedUserIds?.length ? (
            <UsersListSelectedUsers
              allowGuestUsers={true}
              allowSelf={true}
              onLoadedUsers={(users) =>
                setUserIds(users.map((user) => user.id))
              }
              userSelectionParams={{ selectedUserIds }}
            />
          ) : null}

          <FormGroup label={messages.commonComment}>
            <TextArea
              id={`participation-comment-${user?.id || 0}`}
              field="participatedComment"
            />
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventParticipantsToggleParticipation;
